import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import type { DashFilterName } from "@mirage/analytics/events/enums/dash_filter_name";
import { PAP_Select_DashFilter } from "@mirage/analytics/events/types/select_dash_filter";
import type { Connectors } from "@mirage/service-connectors/service";
import {
  SearchFilterType,
  SearchFilterTypeToPAPDashFilterType,
  type SearchFilter,
} from "@mirage/shared/search/search-filters";
import { useCallback } from "react";
import type { CategoryConfig } from "../CategorySearchAccordion/config";
import { AppFilterGroup } from "./AppFilterGroup";
import { ContentTypeRows } from "./ContentTypeRow";
import { useFilterSidebarView } from "./useFilterSidebarView";

type Props = {
  query: string;
  seeAllForFilter: (filter: SearchFilter[]) => void;
  categories: CategoryConfig[];
  connectors: Connectors;
  hasResults?: () => void;
  shouldSkipCache?: boolean;
};

export const FilterSidebar = ({
  query,
  seeAllForFilter,
  categories,
  connectors,
  hasResults,
  shouldSkipCache,
}: Props) => {
  const filterSidebarView = useFilterSidebarView();
  const { reportPapEvent, searchAttemptSessionManager, searchSessionManager } =
    useMirageAnalyticsContext();

  const getHasQueryAndUpdateSession = useCallback(() => {
    let hasQuery = false;
    const searchAttemptSession = searchAttemptSessionManager.getSession();
    if (searchAttemptSession) {
      const query = searchAttemptSession?.properties.query;
      hasQuery = query?.length > 0;

      if (hasQuery) {
        searchSessionManager.updateProperties({
          hasQuery: true,
        });
      }
    }
    return hasQuery;
  }, [searchAttemptSessionManager, searchSessionManager]);

  const logFilterSelected = useCallback(
    (searchFilter: SearchFilter) => {
      const dashFilterType =
        SearchFilterTypeToPAPDashFilterType[searchFilter.type];
      // typeahead filter name and filter UI uses "messages"
      // but existing telemetry uses "chat"
      const filterLoggingId =
        searchFilter.type === SearchFilterType.ContentType &&
        searchFilter.id === "messages"
          ? "chat"
          : (searchFilter.id as DashFilterName);

      searchSessionManager.extendOrCreateSession("select_filter");
      const hasQuery = getHasQueryAndUpdateSession();
      reportPapEvent(
        PAP_Select_DashFilter({
          dashFilterName: filterLoggingId,
          dashFilterType,
          hasQuery,
          isTypeahead: false,
          featureLine: "search",
          actionSurface: "serp",
          actionSurfaceComponent: "serp_filter_sidebar",
          searchSessionId: searchSessionManager.getSessionIdOrUndefined(),
        }),
      );
    },
    [reportPapEvent, searchSessionManager, getHasQueryAndUpdateSession],
  );

  const handleSeeAllForFilter = useCallback(
    (searchFilter: SearchFilter) => {
      logFilterSelected(searchFilter);
      seeAllForFilter([searchFilter]);
    },
    [logFilterSelected, seeAllForFilter],
  );

  if (!filterSidebarView.contentType && !filterSidebarView.app) {
    return null;
  }

  return (
    <div>
      {filterSidebarView.app && (
        <AppFilterGroup
          query={query}
          connectors={connectors}
          seeAllForFilter={handleSeeAllForFilter}
          shouldSkipCache={shouldSkipCache}
        />
      )}
      {filterSidebarView.contentType && (
        <ContentTypeRows
          query={query}
          seeAllForFilter={handleSeeAllForFilter}
          categories={categories}
          hasResults={hasResults}
          shouldSkipCache={shouldSkipCache}
        />
      )}
    </div>
  );
};
